<script setup lang="ts">
import { Layers, X as XIcon } from '@lucide/vue'

import LayersList from '@/components/map/LayersList.vue'
import MapZoomIndicator from '@/components/map/MapZoomIndicator.vue'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * Desktop floating Map Layers panel (a docked card). Mobile uses MapToolSheet (a non-modal bottom
 * sheet) instead; the category list itself is the shared LayersList component. The chrome mirrors the
 * mobile sheet — white surface, rounded-2xl, "Map layers" title, X to close.
 */
defineProps<{ isVisible: boolean }>()
const emit = defineEmits<{ 'toggle-visibility': [] }>()
const closePanel = () => emit('toggle-visibility')
</script>

<template>
  <div
    v-if="isVisible"
    class="absolute bottom-24 right-2 flex max-h-112 w-72 flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_14px_30px_rgba(15,23,42,0.16)]"
    :style="{ zIndex: Z_INDEX.MAP_LAYERS_PANEL }"
  >
    <!-- Header: title + close (matches the mobile sheet's header) -->
    <div class="flex shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2.5">
      <div class="flex items-center gap-2">
        <Layers class="h-4 w-4 text-gray-600" />
        <span class="text-sm font-semibold tracking-tight text-gray-900">Map layers</span>
        <MapZoomIndicator />
      </div>
      <button
        type="button"
        class="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:cursor-pointer hover:bg-gray-100 hover:text-gray-800"
        aria-label="Close"
        @click="closePanel"
      >
        <XIcon class="h-4 w-4" />
      </button>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto p-2">
      <LayersList />
    </div>
  </div>
</template>
